import * as Sentry from "@sentry/react";

import type { WsFeed } from "@storyboard/web-client";
import { serverNow } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("bootstrap");

import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { markBootBegan, markBootInteractive } from "@/init/bootTelemetry";
import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import {
  BOOTSTRAP_MAX_PAGE_SIZE,
  BootstrapEventListType,
  bootstrapItem,
  fetchLatestBootstrapEventDate,
  insertBootstrapEvent,
  nextBootstrapPageArgs,
} from "@/models/BootstrapUtils.ts";
import { accumulateBootstrapDelta, emptyBootstrapDelta } from "@/models/bootstrapDelta";
import { resetFeedPageFlagsForBoot } from "@/models/repositories/Feed";
import { UserContext } from "@/models/StateProviders/userProvider";
import { ResetCoordinator } from "@/services/ResetCoordinator.ts";
import { addServerRowCount, recoverFromBootstrapDivergence } from "@/services/StorageDivergenceRecovery.ts";
import { beginBootPhase, setSessionAttribute, setSessionAttributes } from "@/services/telemetry";
import {
  type BootTrigger,
  REBOOTSTRAP_MANIFEST,
  runBootTask,
  setBootRunKind,
  skipBootTask,
  startBootRun,
} from "@/store/bootRun";
import { store } from "@/store/store";
import { safeSetItem } from "@/utils/localStorage";
import { consumeUnreadAlsoSetAsReadOnNextBootstrap, initializeUnreadsForAllFeeds } from "./actions/calculateUnreads";
import { initializeFeedLatestActivity, initializeFromBot } from "./actions/initialFeedLoad";
import { initializeFromAlias, initializeIsAliasFeed } from "./feedAliasMetadata";
import { MyAccountContext } from "./StateProviders/myAccountProvider";
import { actions, TelemetryContext } from "./TelemetryProvider";

function workspaceSizeBucket(totalRows: number): string {
  if (totalRows < 20_000) return "small";
  if (totalRows < 100_000) return "medium";
  return "large";
}

/**
 * The size bucket is only measurable on a full bootstrap (incremental runs return partial rows),
 * so it's persisted per workspace and re-applied on warm/incremental boots. Without this, most
 * sessions (warm boots) would have no bucket — or worse, a stale one from a previous workspace.
 */
function sizeBucketStorageKey(workspaceId: string): string {
  return `workspaceSizeBucket:${workspaceId}`;
}

function readStoredSizeBucket(workspaceId: string | undefined): string | undefined {
  if (!workspaceId) return undefined;
  try {
    return localStorage.getItem(sizeBucketStorageKey(workspaceId)) ?? undefined;
  } catch {
    return undefined;
  }
}

function applySizeBucket(bucket: string | undefined): void {
  setSessionAttributes({ "workspace.size_bucket": bucket });
  Sentry.setTag("workspaceSizeBucket", bucket);
}

/**
 * A cold bootstrap did not finish: the store holds the pages that landed and nothing more, so the view shows the
 * failure rather than a partial workspace. An incremental run that fails resolves instead and continues on what it has.
 */
export class ColdBootstrapFailedError extends Error {
  constructor() {
    super("Cold bootstrap failed");
    this.name = "ColdBootstrapFailedError";
  }
}

export default function useBootstrap() {
  const { trackAction, setAttribute, finishAction } = useContext(TelemetryContext);
  const params = useParams();
  const [invalidWorkspaceError] = useState<boolean>(false);
  const [bootstrapLoading] = useState<boolean>(false);
  const [allComplete, setAllComplete] = useState<boolean>(false);
  const [localAllComplete, setLocalAllComplete] = useState<boolean>(false);
  const [feedIds] = useState(new Map<string, boolean>());
  // Server-returned row totals per tableName for the current bootstrap run; the post-bootstrap
  // divergence probe compares them against local collection sizes (WEB-2152).
  const [serverRowCounts] = useState(new Map<string, number>());
  const bootstrapModeRef = useRef<"full" | "incremental">("full");
  // Feeds touched by the current run's deltas; incremental boots scope the post-bootstrap
  // recompute to this set instead of re-running it across every collection (WEB-2202).
  const bootstrapDeltaRef = useRef(emptyBootstrapDelta());
  const lastBootstrapRef = useRef<{ bootstrapItems: BootstrapEventListType[]; workspaceId?: string } | null>(null);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const { workspaceMembership: myWorkspaceMembership } = useContext(UserContext);

  const { myAccount } = useContext(MyAccountContext);

  const [startTime] = useState(() => Date.now());

  const recursiveBootstrapCall = async (args: {
    workspaceId: string;
    bootstrapEvent: BootstrapEventListType;
    page: number;
    pageSize: number;
    accountId: string;
    slowLoad: boolean;
    mine?: boolean;
    bootstrapItemsSize?: number;
    timestamp?: string;
    /** The server's clock as the entry's first page went out; the cursor its last page stamps. */
    startedAt: Date;
  }) => {
    const { workspaceId, bootstrapEvent, page, pageSize = BOOTSTRAP_MAX_PAGE_SIZE, mine, timestamp, startedAt } = args;
    // No span around the network call itself: the web-client request layer already emits an
    // `api`-category span per page with the route, status, pagination, rows, and boot phase.
    try {
      const payload = timestamp
        ? {
            bootstrapEvent,
            workspaceId,
            page,
            pageSize,
            mine,
            timestamp,
          }
        : {
            bootstrapEvent,
            workspaceId,
            page,
            pageSize,
            mine,
          };

      const { data: responseData, pageSize: effectivePageSize } = await bootstrapItem(payload);
      // Adopt the server's clamped pageSize so a full page still triggers the next request.
      const pageSizeForHasMore = effectivePageSize < pageSize ? effectivePageSize : pageSize;
      // Fire-and-forget, like the per-table marker below: nothing in this run reads the marker
      // back, and awaiting it put the registry's ~43.5s open-retry budget in front of every page
      // of every table whenever the open is timing out.
      void insertBootstrapEvent("bootstrap", workspaceId);

      if (bootstrapEvent.bootstrapFunctionName === "bootstrapWorkspaceFeedPermissions") {
        log.info("bootstrapWorkspaceFeedPermissions", responseData, {
          workspaceId,
          page,
          pageSize: pageSizeForHasMore,
          mine,
        });
      }

      addServerRowCount(serverRowCounts, bootstrapEvent.tableName, responseData?.length ?? 0);
      accumulateBootstrapDelta(
        bootstrapDeltaRef.current,
        bootstrapEvent.tableName,
        (responseData ?? []) as unknown as Parameters<typeof accumulateBootstrapDelta>[2],
      );

      if (bootstrapEvent.feed) {
        const feeds: WsFeed[] = responseData as WsFeed[];
        for (const feed of feeds) {
          feedIds.set(feed.id, true);
        }
      }

      if (responseData?.length > 0) {
        const subSpan = trackAction(actions.bootstrapDbWrite(bootstrapEvent.bootstrapFunctionName, page));

        subSpan.setAttribute("bootstrap.function", bootstrapEvent.bootstrapFunctionName);
        subSpan.setAttribute("bootstrap.page", page);
        subSpan.setAttribute("bootstrap.pageSize", pageSizeForHasMore);
        subSpan.setAttribute("bootstrap.responseSize", responseData.length);

        // One store batch per page: listeners fire once regardless of row count.
        await store.batch(() => bootstrapEvent.createManyFunctionName(responseData));
        finishAction(actions.bootstrapDbWrite(bootstrapEvent.bootstrapFunctionName, page));
      } else {
        log.info(
          "No data found for bootstrap event",
          bootstrapEvent.bootstrapFunctionName,
          bootstrapEvent.bootstrapFunctionCallResponseName,
        );
      }

      if (responseData?.length >= pageSizeForHasMore && bootstrapEvent.recursive !== false) {
        return await recursiveBootstrapCall(nextBootstrapPageArgs(args, pageSizeForHasMore));
      }
      // Stamped once, when the entry's last page has landed, at the instant its first page went out: a cursor
      // stamped per page at the client's clock could sit past a row changed while the pages were read, and a delta
      // from it would skip that row.
      void insertBootstrapEvent(bootstrapEvent.bootstrapFunctionCallResponseName, bootstrapEvent.tableName, startedAt);
      return responseData?.length ?? 0;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const updateLocalFeed = async () => {
    trackAction(actions.bootstrapAfterHooksInitialUnreadItems());
    setAttribute("accountId", myAccount?.id);

    const feedKeys = Array.from(feedIds.keys());

    if (!myWorkspaceMembership.accountId) {
      throw new Error("Account ID is required");
    }

    const alsoSetAsRead = consumeUnreadAlsoSetAsReadOnNextBootstrap();
    // Incremental boots scope the recompute to what their deltas touched (WEB-2202): the alias
    // initializers only need to run when alias-defining tables (feeds, permissions, command
    // aliases) changed — item writes already compute per-item alias/bot membership and feed
    // activity — and the unread pass takes the touched feed set. An alsoSetAsRead reload keeps
    // the full pass (its point is reconciling every feed), as do cold boots and divergence
    // recovery.
    const delta = bootstrapDeltaRef.current;
    const scoped = bootstrapModeRef.current === "incremental" && !alsoSetAsRead;
    const runAliasInitializers = !scoped || delta.aliasMetadataTouched;
    const unreadFeedIds = scoped ? [...delta.feedIds] : undefined;
    const runUnreads = unreadFeedIds === undefined || unreadFeedIds.length > 0;

    if (runAliasInitializers || runUnreads) {
      await store.batch(async () => {
        if (runAliasInitializers) {
          await initializeIsAliasFeed();
          await initializeFromAlias();
          await initializeFromBot();
          await initializeFeedLatestActivity();
        }
        if (runUnreads) {
          await initializeUnreadsForAllFeeds({
            membership: {
              ...myWorkspaceMembership,
              accountId: myWorkspaceMembership.accountId ?? "",
            },
            feedIds: unreadFeedIds,
            alsoSetAsRead,
          });
        }
      });
    }

    for (const id of feedKeys) {
      feedIds.delete(id);
    }
    finishAction(actions.bootstrapAfterHooksInitialUnreadItems());
  };

  /**
   * Post-bootstrap divergence probe (WEB-2152): verifies the store holds the rows the server
   * returned, re-running a full bootstrap through this hook once per workspace per page load when
   * it doesn't. The outcome needs no handling here — divergence is reported, never escalated to a
   * wipe or an error screen, so there is nothing left for the caller to render (WEB-2252).
   */
  const verifyBootstrapIntegrity = async () => {
    const lastBootstrap = lastBootstrapRef.current;
    if (!lastBootstrap) return;
    // A full bootstrap's server row totals are the best available workspace-size proxy; stamp the
    // bucket on all subsequent spans so Honeycomb queries can segment by workspace size, and
    // persist it so warm/incremental boots of this workspace inherit an accurate value.
    if (bootstrapModeRef.current === "full") {
      const totalRows = [...serverRowCounts.values()].reduce((sum, rows) => sum + rows, 0);
      const sizeBucket = workspaceSizeBucket(totalRows);
      applySizeBucket(sizeBucket);
      if (lastBootstrap.workspaceId) {
        safeSetItem(localStorage, sizeBucketStorageKey(lastBootstrap.workspaceId), sizeBucket);
      }
    }
    await recoverFromBootstrapDivergence({
      // Snapshot so a workspace switch clearing/repopulating the shared map mid-probe can't mix
      // counts from two runs.
      serverCounts: new Map(serverRowCounts),
      workspaceId: lastBootstrap.workspaceId ?? null,
      bootstrapMode: bootstrapModeRef.current,
      superseded: () => lastBootstrapRef.current !== lastBootstrap,
      rebootstrap: () =>
        bootstrapPaginatedApplication({
          bootstrapItems: lastBootstrap.bootstrapItems,
          workspaceId: lastBootstrap.workspaceId,
          forceBootstrap: true,
          trigger: "rebootstrap",
        }),
    });
  };

  /** Runs the paged bootstrap calls; resolves false (after logging) when any of them failed. */
  const bootstrapTransactions = async ({
    workspaceId,
    bootstrapItems,
    slowLoad,
  }: {
    workspaceId: string;
    bootstrapItems: BootstrapEventListType[];
    slowLoad: boolean;
  }): Promise<boolean> => {
    const bootstrapItemsWithTimestamp = await Promise.all(
      bootstrapItems?.map(async (item) => {
        const hasPreviousTimestamp = await fetchLatestBootstrapEventDate(item.bootstrapFunctionCallResponseName);
        const timestamp = hasPreviousTimestamp ? new Date(hasPreviousTimestamp).toISOString() : null;
        return {
          ...item,
          timestamp,
        };
      }),
    );

    const startedAt = serverNow();
    const bootstrapPromises: Promise<number>[] = [];
    for (const bootstrapEvent of bootstrapItemsWithTimestamp) {
      bootstrapPromises.push(
        recursiveBootstrapCall({
          workspaceId,
          bootstrapEvent,
          page: 0,
          pageSize: bootstrapEvent.pageSize,
          accountId: myAccount?.id,
          mine: bootstrapEvent.mine,
          bootstrapItemsSize: bootstrapItems.length,
          timestamp: bootstrapEvent.timestamp || undefined,
          slowLoad,
          startedAt,
        }),
      );
    }

    return await Promise.all(bootstrapPromises)
      .then(() => true)
      .catch((error) => {
        log.error("Bootstrap transactions failed", { error });
        return false;
      });
  };

  const bootstrapPaginatedApplication = async ({
    bootstrapItems,
    workspaceId,
    forceBootstrap,
    trigger = "boot",
  }: {
    bootstrapItems: BootstrapEventListType[];
    workspaceId?: string;
    forceBootstrap?: boolean;
    /** `rebootstrap` when the divergence probe re-runs the bootstrap; it runs in full mode but never blocks. */
    trigger?: BootTrigger;
  }) => {
    if (bootstrapLoading && !forceBootstrap) {
      return;
    }
    setAllComplete(false);
    setLocalAllComplete(false);
    feedIds.clear();
    serverRowCounts.clear();
    bootstrapDeltaRef.current = emptyBootstrapDelta();
    lastBootstrapRef.current = { bootstrapItems, workspaceId };
    // A re-bootstrap is its own run: the boot's warmup calls never re-run, and the rail shows it as starting over.
    // A boot's run is started by the provider that calls this.
    if (trigger === "rebootstrap") startBootRun({ trigger: "rebootstrap", manifest: REBOOTSTRAP_MANIFEST });
    setActiveWorkspaceId(() => workspaceId);
    setAttribute("accountId", myAccount?.id);
    setAttribute("workspaceId", workspaceId);
    markBootBegan();
    trackAction(actions.bootstrapFull());
    trackAction(actions.bootstrapLoad());

    // The store boot already reconciled the registry against the restored snapshot (clearing it
    // when no usable snapshot existed), so the registry claim alone decides full vs incremental.
    const registry = BootstrapEventsRegistry.getInstance();
    const hasExistingBootstrapEvent = await registry.hasBootstrapForWorkspace(workspaceId ?? undefined);
    const slowLoad = !hasExistingBootstrapEvent;
    bootstrapModeRef.current = slowLoad ? "full" : "incremental";
    // Drop the page flags this boot cannot trust before warmup reads them. Every boot clears
    // `loadedLastPage`: it was true when set, but nothing kept it true while the app was closed,
    // and warmup skips flagged feeds — so a warm boot left it set never caught them up (WEB-2355).
    // Only a full boot also clears `loadedFirstPage`; an incremental one keeps the history it holds.
    // This knowingly gives up WEB-2202's warm-boot skip: every warmup candidate refetches its newest
    // page on each boot, because nothing short of that fetch says what arrived while it was closed.
    resetFeedPageFlagsForBoot(bootstrapModeRef.current);
    // beginBootPhase maps the bootstrap mode to the phase: a full paged bootstrap → `cold`, an
    // incremental run → `warm` (first this page load) or `incremental`. The `bootstrap.full` action
    // fires for both modes, so it is not itself a cold indicator — the phase is the source of truth.
    const bootPhase = beginBootPhase(bootstrapModeRef.current);
    // The run's kind decides the rail's look: only a cold boot blocks.
    setBootRunKind(bootPhase);
    setSessionAttributes({ "workspace.id": workspaceId });
    // Also stamp phase + workspace on the action-span attribute map: the perceived `bootstrap.full` /
    // `bootstrap.load` spans are created before this point (so they miss the start-time session
    // stamp) but finish after, and finishAction merges this map onto the span at end.
    setAttribute("boot.phase", bootPhase);
    setAttribute("workspace.id", workspaceId);
    // Only set account.id when we actually have it: `myAccount` resolves via a reactive query that
    // often hasn't landed at this point, and passing undefined would *clear* the id already stamped
    // from the auth session, which is what left it 0%-populated. MyAccountProvider refreshes it once
    // the local record loads.
    if (myAccount?.id) setSessionAttribute("account.id", myAccount.id);
    // Searchable tags so any later Sentry event carries the boot/workspace context.
    Sentry.setTag("workspaceId", workspaceId);
    Sentry.setTag("bootPhase", bootPhase);
    // Re-apply this workspace's stored bucket (or clear it) so a workspace switch can't leak the
    // previous workspace's bucket; a full bootstrap refreshes the stored value below.
    applySizeBucket(readStoredSizeBucket(workspaceId));

    Sentry.setContext("bootstrap", {
      mode: slowLoad ? "full" : "incremental",
      boot_phase: bootPhase,
      workspace_id: workspaceId,
      bootstrap_items: bootstrapItems.length,
    });

    // The paged run (and an incremental run's feed pass) is the boot work the rail indicator tracks.
    const runPagedBootstrap = async () => {
      // `bootstrapTransactions` resolves false for a failed paged call, but its timestamp
      // resolution runs outside that guard. Treating a throw as a failed run keeps the caller's
      // continuation reachable — without it a rejection here strands every feed on `<Loading />`
      // with a reload as the only way out (WEB-2245).
      const succeeded = await bootstrapTransactions({
        workspaceId,
        bootstrapItems,
        slowLoad,
      }).catch((error) => {
        log.error("Bootstrap transactions threw", { error });
        return false;
      });
      if (!succeeded && slowLoad) {
        finishAction(actions.bootstrapFull(), { error: "Paged bootstrap failed" });
        // Rejecting with the typed error is what reaches the caller's failure surface: resolving here left the view on
        // `<Loading />` with nothing logged at the surface that pages anyone, and the live data paths never started.
        throw new ColdBootstrapFailedError();
      }
      // A failed run may have fetched rows it never wrote; wiping the counts keeps the divergence
      // probe from reasoning about an unverified bootstrap and burning a recovery tier on a
      // transient write error.
      if (!succeeded) serverRowCounts.clear();
      if (slowLoad) {
        setLocalAllComplete(() => true);
        finishAction(actions.bootstrapFull());
        return;
      }
      await updateLocalFeed();
      setLocalAllComplete(() => true);
      finishAction(actions.bootstrapFull());
    };
    await runBootTask("bootstrapping", "bootstrap", runPagedBootstrap());
  };

  useEffect(() => {
    if (feedIds.size > 0 && localAllComplete && myAccount) {
      const postPass = updateLocalFeed()
        .then(() => {
          finishAction(actions.bootstrapLoad());
          log.info("DONE: bootstrap complete", Date.now() - startTime);
          setAllComplete(true);
          markBootInteractive();
          ResetCoordinator.getInstance()
            .start()
            .then(() => {});
          runBootTask("warming", "bootstrap.integrity", verifyBootstrapIntegrity()).catch((e) =>
            log.warn("Divergence probe failed", { error: e }),
          );
        })
        .catch((e) => {
          finishAction(actions.bootstrapLoad(), { error: e.message });
          finishAction(actions.bootstrapFull(), { error: e.message });
          log.error("Error in updateLocalFeed", e);
        });
      void runBootTask("warming", "bootstrap.postpass", postPass);
    } else if (feedIds?.size === 0 && localAllComplete && myAccount) {
      // No feeds, no post-pass: the run does not wait for one.
      skipBootTask("warming", "bootstrap.postpass");
      finishAction(actions.bootstrapLoad());
      log.info("DONE: bootstrap complete (no feeds)", Date.now() - startTime);
      setAllComplete(true);
      markBootInteractive();
      ResetCoordinator.getInstance()
        .start()
        .then(() => {});
      runBootTask("warming", "bootstrap.integrity", verifyBootstrapIntegrity()).catch((e) =>
        log.warn("Divergence probe failed", { error: e }),
      );
    }
  }, [feedIds, params, localAllComplete, myAccount]);

  return {
    invalidWorkspaceError,
    allComplete,
    bootstrapPaginatedApplication,
    activeWorkspaceId,
  };
}
